import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import { hasOpenModal } from '@/components/ui/Modal';
import { Command } from './types';

/** Cap on how far back the stack remembers (per direction). */
const LIMIT = 100;

interface HistoryCtx {
  /** Register an action that just succeeded, with its inverse. Clears the redo stack. */
  record: (cmd: Command) => void;
  undo: () => void;
  redo: () => void;
  /** Jump so the undo stack ends up `targetPastLen` deep (multi-step undo/redo). */
  seek: (targetPastLen: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  /** Oldest→newest done actions and next→furthest undone actions, for the history list. */
  past: Command[];
  future: Command[];
  /** True while an undo/redo/seek is in flight (buttons + shortcuts are inert then). */
  busy: boolean;
  /** Bumped after every undo/redo/seek; views include it in their reload deps. */
  dataVersion: number;
  /** Show a transient toast (auto-dismisses). Reused for lightweight feedback like "Link copied". */
  showToast: (text: string, kind?: 'info' | 'error', action?: { label: string; run: () => void }) => void;
}

const Ctx = createContext<HistoryCtx | null>(null);

export const useHistory = (): HistoryCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useHistory must be used within HistoryProvider');
  return v;
};

type Toast = { id: number; text: string; kind: 'info' | 'error'; action?: { label: string; run: () => void } };

/** Is the event target a field where the browser's OWN undo (text editing) should win? */
const isEditable = (t: EventTarget | null): boolean => {
  const n = t as HTMLElement | null;
  return !!n?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
};

/**
 * A global, cross-view undo/redo stack for item actions. Actions record their own
 * inverse via `record`; undo/redo call the API and then bump `dataVersion` so the
 * mounted view reloads from server truth (each item view lists its own items, so a
 * shared reload signal is simpler and more robust than reaching into their state).
 *
 * Must sit inside AppDataProvider — it switches the current board so an undo from a
 * different board is visible where it happened.
 */
export const HistoryProvider = ({ children }: { children: ReactNode }) => {
  const { boards, currentBoardId, setCurrentBoardId } = useAppData();

  const [past, setPast] = useState<Command[]>([]);
  const [future, setFuture] = useState<Command[]>([]);
  const [busy, setBusy] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastSeq = useRef(0);

  // Mirror state into refs so the once-bound key handler and the async runners read
  // the live stacks without being re-created on every push.
  const pastRef = useRef(past);
  pastRef.current = past;
  const futureRef = useRef(future);
  futureRef.current = future;
  // busyRef is AUTHORITATIVE, not a render mirror: it's flipped synchronously at the
  // start/end of every runner so `record` and re-entrant runs are gated the instant a
  // run begins (a render-mirrored flag lags a tick and would let a concurrent action's
  // record() interleave and corrupt the stacks).
  const busyRef = useRef(false);
  // Live current board, so a multi-step seek that crosses boards always compares against
  // the real current board (a captured currentBoardId would go stale mid-loop).
  const boardIdRef = useRef(currentBoardId);
  boardIdRef.current = currentBoardId;

  const showToast = useCallback((text: string, kind: Toast['kind'] = 'info', action?: Toast['action']) => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, text, kind, action });
  }, []);

  // Auto-dismiss the toast; a fresh toast (new id) restarts the timer.
  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const switchBoard = useCallback(
    (boardId: string | null) => {
      if (boardId && boardId !== boardIdRef.current && boards.some((b) => b.id === boardId)) {
        boardIdRef.current = boardId; // reflect immediately so a seek loop won't re-switch
        setCurrentBoardId(boardId);
      }
    },
    [boards, setCurrentBoardId],
  );

  const record = useCallback((cmd: Command) => {
    // Never mutate the stacks while a runner is mid-flight: the runner mutates them
    // positionally around its own await, and an interleaved push would corrupt them.
    // The action still persisted server-side; it just isn't separately undoable.
    if (busyRef.current) return;
    setPast((p) => [...p, cmd].slice(-LIMIT));
    setFuture([]);
  }, []);

  const undo = useCallback(async () => {
    if (busyRef.current) return;
    const cmd = pastRef.current[pastRef.current.length - 1];
    if (!cmd) return;
    busyRef.current = true;
    setBusy(true);
    try {
      switchBoard(cmd.boardId);
      await cmd.undo();
      // Remove by identity, not position, so this stays correct even if the stack
      // shifted underneath us.
      setPast((p) => p.filter((c) => c !== cmd));
      setFuture((f) => [cmd, ...f].slice(0, LIMIT));
      showToast(`Undid ${cmd.label}`, 'info', { label: 'Redo', run: () => redo() });
    } catch {
      // Leave the command on the undo stack (nothing moved) so it can be retried;
      // reload to reconcile the view with server truth.
      showToast(`Couldn't undo ${cmd.label}`, 'error');
    } finally {
      setDataVersion((v) => v + 1);
      busyRef.current = false;
      setBusy(false);
    }
    // redo is referenced for the toast action; declared below and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchBoard, showToast]);

  const redo = useCallback(async () => {
    if (busyRef.current) return;
    const cmd = futureRef.current[0];
    if (!cmd) return;
    busyRef.current = true;
    setBusy(true);
    try {
      switchBoard(cmd.boardId);
      await cmd.redo();
      setFuture((f) => f.filter((c) => c !== cmd));
      setPast((p) => [...p, cmd].slice(-LIMIT));
      showToast(`Redid ${cmd.label}`, 'info', { label: 'Undo', run: () => undo() });
    } catch {
      showToast(`Couldn't redo ${cmd.label}`, 'error');
    } finally {
      setDataVersion((v) => v + 1);
      busyRef.current = false;
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchBoard, showToast]);

  // Multi-step jump used by the history list: undo down to (or redo up to) a target
  // depth, applying each inverse in sequence and reloading only once at the end. The
  // refs are advanced manually inside the loop so each step sees the previous result.
  const seek = useCallback(
    async (targetPastLen: number) => {
      if (busyRef.current) return;
      const clamp = Math.max(0, Math.min(targetPastLen, pastRef.current.length + futureRef.current.length));
      if (clamp === pastRef.current.length) return;
      busyRef.current = true;
      setBusy(true);
      try {
        while (pastRef.current.length > clamp) {
          const cmd = pastRef.current[pastRef.current.length - 1];
          if (!cmd) break;
          switchBoard(cmd.boardId);
          await cmd.undo();
          const np = pastRef.current.slice(0, -1);
          const nf = [cmd, ...futureRef.current].slice(0, LIMIT);
          pastRef.current = np;
          futureRef.current = nf;
          setPast(np);
          setFuture(nf);
        }
        while (pastRef.current.length < clamp && futureRef.current.length) {
          const cmd = futureRef.current[0];
          switchBoard(cmd.boardId);
          await cmd.redo();
          const nf = futureRef.current.slice(1);
          const np = [...pastRef.current, cmd].slice(-LIMIT);
          futureRef.current = nf;
          pastRef.current = np;
          setFuture(nf);
          setPast(np);
        }
      } catch {
        showToast(`Couldn't reach that point in history`, 'error');
      } finally {
        setDataVersion((v) => v + 1);
        busyRef.current = false;
        setBusy(false);
      }
    },
    [switchBoard, showToast],
  );

  // Keyboard: ⌘/Ctrl+Z undo, ⌘/Ctrl+Shift+Z or Ctrl+Y redo — but never while a text
  // field is focused, so native text-editing undo keeps working there.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || !(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      const isUndo = k === 'z' && !e.shiftKey;
      const isRedo = (k === 'z' && e.shiftKey) || k === 'y';
      if (!isUndo && !isRedo) return;
      if (isEditable(e.target)) return;
      // Stand down while a dialog/drawer is open: an undo behind it (whose toast the
      // modal would cover, and whose board-switch could retarget the open drawer) is
      // confusing. Close the dialog first, then undo.
      if (hasOpenModal()) return;
      e.preventDefault();
      if (isUndo) undo();
      else redo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return (
    <Ctx.Provider
      value={{
        record,
        undo,
        redo,
        seek,
        canUndo: past.length > 0,
        canRedo: future.length > 0,
        undoLabel: past.length ? past[past.length - 1].label : null,
        redoLabel: future.length ? future[0].label : null,
        past,
        future,
        busy,
        dataVersion,
        showToast,
      }}
    >
      {children}
      {toast ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[2200] flex justify-center px-4">
          <div
            role="status"
            className={cnToast(toast.kind)}
          >
            <span className="truncate">{toast.text}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  const run = toast.action!.run;
                  setToast(null);
                  run();
                }}
                className="pointer-events-auto -my-1 ml-1 shrink-0 rounded-md px-2 py-1 text-[12px] font-semibold text-accent transition-colors hover:bg-white/10"
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Ctx.Provider>
  );
};

/** Toast pill styling — a high-contrast chip that reads in both themes. */
const cnToast = (kind: Toast['kind']): string =>
  'pointer-events-auto flex max-w-[min(92vw,460px)] items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium shadow-xl shadow-black/40 ' +
  (kind === 'error'
    ? 'bg-danger text-white'
    : 'bg-fg text-ink');
